/**
 * Batch #14.17 — Admin : Documents à signer (envoyer un modèle PDF à un client).
 * Batch #14.236 — Ajout : Mandat fiscal « Formulaire de renseignements » (à remplir + signer,
 *                 magic link et/ou portail).
 *
 * Route admin : /admin/client-documents
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import {
  FileSignature, Send, Trash2, Download, Loader2,
  CheckCircle2, Clock, X, Plus, FileText, Mail, User, ClipboardList, Link2, Copy, Users, Eye, Smartphone, Archive, ArchiveRestore,
} from "lucide-react";
import { toast } from "@/lib/toast";
import DriveSendButton from "@/components/admin/DriveSendButton";
import { ServiceContractEditor } from "@/pages/admin/documents/ServiceContractEditor";
import { QuestionnaireEditor } from "@/pages/admin/documents/QuestionnaireEditor";
import { API, authH } from "@/api";
import { useReadOverrides } from "@/hooks/useReadOverrides";
import PdfTemplatesTab from "@/pages/admin/pdfTemplates/PdfTemplatesTab";
import BulkMandateModal from "@/pages/admin/documents/BulkMandateModal";
import SmsSendModal from "@/components/admin/SmsSendModal";
import SmsOptIn from "@/components/admin/SmsOptIn";

const FISCAL_FORM = "__fiscal_form__";
const TAX_REPORT = "__tax_report__";
const SERVICE_CONTRACT = "__service_contract__";
const QUESTIONNAIRE = "__questionnaire__";

const AMOUNT_FIELDS = {
  revenus: [
    ["revenus_total", "Revenus déclarés totaux"],
    ["emploi_fed", "Revenu d'emploi (Fédéral)"],
    ["emploi_qc", "Revenu d'emploi (Québec)"],
    ["autres", "Autres montants (Féd./Qc)"],
  ],
  fed: [
    ["fed_deductions", "Déductions"],
    ["fed_credits", "Crédits non remboursables"],
    ["fed_impot", "Impôt à payer"],
    ["fed_retenues", "Retenues/crédits"],
    ["fed_resultat", "Résultat (montant)"],
  ],
  prov: [
    ["prov_deductions", "Déductions"],
    ["prov_credits", "Crédits non remboursables"],
    ["prov_impot", "Impôt à payer"],
    ["prov_retenues", "Retenues/crédits"],
    ["prov_resultat", "Résultat (montant)"],
  ],
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-CA", { dateStyle: "medium" }); }
  catch { return iso; }
}

function StatusBadge({ status }) {
  const m = {
    pending: { bg: "bg-amber-100", fg: "text-amber-700", label: "En attente", icon: <Clock className="w-3 h-3" /> },
    signed: { bg: "bg-emerald-100", fg: "text-emerald-700", label: "Signé", icon: <CheckCircle2 className="w-3 h-3" /> },
    completed: { bg: "bg-emerald-100", fg: "text-emerald-700", label: "Rempli et signé", icon: <CheckCircle2 className="w-3 h-3" /> },
  };
  const s = m[status] || { bg: "bg-slate-100", fg: "text-slate-600", label: status, icon: null };
  return (
    <span className={`inline-flex items-center gap-1 ${s.bg} ${s.fg} text-xs font-medium px-2 py-0.5 rounded-full`}>
      {s.icon}{s.label}
    </span>
  );
}

const copyLink = async (link) => {
  try {
    await navigator.clipboard.writeText(link);
    toast.success("Lien copié dans le presse-papier");
  } catch {
    window.prompt("Copiez le lien :", link);
  }
};

// ── Modale création ──────────────────────────────────────────
function CreateDocumentModal({ onClose, onCreated }) {
  const [templates, setTemplates] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [clientId, setClientId] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Batch #14.236 — options mandat fiscal
  const [taxYear, setTaxYear] = useState(new Date().getFullYear() - 1);
  const [prefill, setPrefill] = useState(true);
  const [sendEmail, setSendEmail] = useState(true);
  const [showInPortal, setShowInPortal] = useState(false);
  const [createdLink, setCreatedLink] = useState("");
  // Batch #14.239 — options rapport d'impôt
  const [taxCivility, setTaxCivility] = useState("Madame");
  const [amounts, setAmounts] = useState({ fed_type: "solde", prov_type: "solde", combine_type: "solde" });
  // Batch #14.240 — contrat de services (contenu 100 % éditable)
  const [contract, setContract] = useState(null);
  // Batch #14.242 — questionnaire à remplir + signer
  const [questionnaire, setQuestionnaire] = useState(null);
  // Batch #14.336 — texto avec lien (texte validé par l'admin, {lien} remplacé à l'envoi)
  const [smsOpt, setSmsOpt] = useState({ send_sms: false, sms_text: "", sms_to: "" });

  const isFiscal = templateId === FISCAL_FORM;
  const isTax = templateId === TAX_REPORT;
  const isContract = templateId === SERVICE_CONTRACT;
  const isQuestionnaire = templateId === QUESTIONNAIRE;
  const isSpecial = isFiscal || isTax || isContract || isQuestionnaire;
  const setAmt = (k, v) => setAmounts((a) => ({ ...a, [k]: v }));
  const smsKind = isFiscal ? "fiscal" : isTax ? "tax_report" : isContract ? "contract" : isQuestionnaire ? "questionnaire" : "document";
  const smsTitle = isFiscal ? `Formulaire de renseignements fiscaux ${taxYear}` : isTax ? `Rapport d'impôt ${taxYear}` : isContract ? contract?.title : isQuestionnaire ? questionnaire?.title : (templates.find((t) => t.id === templateId)?.name || "");
  const smsPayload = { send_sms: smsOpt.send_sms, sms_text: smsOpt.sms_text, sms_to: smsOpt.sms_to };
  const smsToast = (data) => {
    if (!smsOpt.send_sms) return;
    if (data.sms_sent) toast.success(`Texto envoyé à ${data.sms_to}`);
    else toast.error(`Texto non envoyé : ${data.sms_error || "erreur"}`);
  };
  const smsOptIn = (accent) => (
    <SmsOptIn previewBody={{ kind: smsKind, client_id: clientId, title: smsTitle }} checked={smsOpt.send_sms} onChange={setSmsOpt} accent={accent} testId={`${smsKind}-sms-optin`} />
  );

  // Charge le gabarit par défaut du questionnaire à la sélection
  useEffect(() => {
    if (!isQuestionnaire || questionnaire) return;
    axios.get(`${API}/admin/questionnaires/defaults`, { headers: authH() })
      .then((r) => setQuestionnaire({ title: r.data.title, sections: r.data.sections, client_company: "" }))
      .catch(() => setQuestionnaire({ title: "QUESTIONNAIRE CLIENT", sections: [], client_company: "" }));
  }, [isQuestionnaire, questionnaire]);

  useEffect(() => {
    if (!isQuestionnaire || !clientId) return;
    const c = allClients.find((x) => x.id === clientId);
    if (c) setQuestionnaire((k) => (k ? { ...k, client_company: c.company_name || "" } : k));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuestionnaire, clientId]);

  // Charge le gabarit par défaut du contrat à la sélection
  useEffect(() => {
    if (!isContract || contract) return;
    axios.get(`${API}/admin/service-contracts/defaults`, { headers: authH() })
      .then((r) => setContract({
        title: r.data.title, sections: r.data.sections, checkboxes: r.data.checkboxes,
        sign_city: r.data.sign_city, client_company: "", client_block: "",
      }))
      .catch(() => setContract({
        title: "CONTRAT DE PRESTATION DE SERVICES", sections: [{ heading: "", body: "" }],
        checkboxes: [], sign_city: "Saguenay", client_company: "", client_block: "",
      }));
  }, [isContract, contract]);

  // Pré-remplit le bloc client du contrat selon le client sélectionné
  useEffect(() => {
    if (!isContract || !clientId || !contract) return;
    const c = allClients.find((x) => x.id === clientId);
    if (!c) return;
    const fullName = `${c.first_name || ""} ${c.last_name || ""}`.trim();
    const lines = [
      c.company_name || fullName,
      c.company_name ? `${fullName} - entreprise individuelle` : "entreprise individuelle",
      c.address,
      [c.city, c.province, c.postal_code].filter(Boolean).join(", "),
      c.phone,
      c.email,
    ].filter(Boolean);
    setContract((k) => ({ ...k, client_company: c.company_name || "", client_block: lines.join("\n") }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isContract, clientId]);

  useEffect(() => {
    (async () => {
      try {
        const [t, c] = await Promise.all([
          axios.get(`${API}/admin/pdf-templates?template_type=document`, { headers: authH() }),
          axios.get(`${API}/clients`, { headers: authH() }),
        ]);
        setTemplates(t.data?.items || []);
        setAllClients(c.data?.items || c.data || []);
      } catch (e) {
        toast.error("Erreur de chargement");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Documents PDF classiques : seulement clients avec portail. Modèles spéciaux : tous les clients.
  const clients = isSpecial ? allClients : allClients.filter((x) => x.portal_enabled);

  const handleSubmit = async () => {
    if (!templateId || !clientId) {
      toast.error("Veuillez sélectionner un modèle et un client");
      return;
    }
    setSubmitting(true);
    try {
      if (isQuestionnaire) {
        const r = await axios.post(`${API}/admin/questionnaires`, {
          client_id: clientId,
          title: questionnaire.title,
          client_company: questionnaire.client_company,
          sections: questionnaire.sections,
          send_email: sendEmail,
          show_in_portal: showInPortal,
          notes,
          origin_url: window.location.origin,
          ...smsPayload,
        }, { headers: authH() });
        toast.success(sendEmail && r.data.email_sent
          ? "Questionnaire créé et courriel envoyé au client"
          : "Questionnaire créé — copiez le lien ci-dessous");
        setCreatedLink(r.data.magic_link);
        smsToast(r.data);
        onCreated();
        return;
      }
      if (isContract) {
        const r = await axios.post(`${API}/admin/service-contracts`, {
          client_id: clientId,
          title: contract.title,
          client_company: contract.client_company,
          client_block: contract.client_block,
          sections: contract.sections,
          checkboxes: contract.checkboxes.filter((c) => c.trim()),
          sign_city: contract.sign_city,
          send_email: sendEmail,
          show_in_portal: showInPortal,
          notes,
          origin_url: window.location.origin,
          ...smsPayload,
        }, { headers: authH() });
        toast.success(sendEmail && r.data.email_sent
          ? "Contrat créé et courriel envoyé au client"
          : "Contrat créé — copiez le lien ci-dessous");
        setCreatedLink(r.data.magic_link);
        smsToast(r.data);
        onCreated();
        return;
      }
      if (isTax) {
        const r = await axios.post(`${API}/admin/tax-report-letters`, {
          client_id: clientId,
          tax_year: Number(taxYear),
          civility: taxCivility,
          amounts,
          send_email: sendEmail,
          show_in_portal: showInPortal,
          notes,
          origin_url: window.location.origin,
          ...smsPayload,
        }, { headers: authH() });
        toast.success(sendEmail && r.data.email_sent
          ? "Rapport créé et courriel envoyé au client"
          : "Rapport créé — copiez le lien ci-dessous");
        setCreatedLink(r.data.magic_link);
        smsToast(r.data);
        onCreated();
        return;
      }
      if (isFiscal) {
        if (!sendEmail && !showInPortal) {
          // Aucun canal : le lien copiable suffit, on continue quand même
        }
        const r = await axios.post(`${API}/admin/fiscal-mandates`, {
          client_id: clientId,
          tax_year: Number(taxYear),
          prefill,
          send_email: sendEmail,
          show_in_portal: showInPortal,
          notes,
          origin_url: window.location.origin,
          ...smsPayload,
        }, { headers: authH() });
        toast.success(sendEmail && r.data.email_sent
          ? "Mandat créé et courriel envoyé au client"
          : "Mandat créé — copiez le lien ci-dessous");
        setCreatedLink(r.data.magic_link);
        smsToast(r.data);
        onCreated();
        return; // on reste ouvert pour montrer le lien
      }
      const rd = await axios.post(`${API}/admin/client-documents`,
        { template_id: templateId, client_id: clientId, notes, ...smsPayload },
        { headers: authH() },
      );
      toast.success("Document envoyé au client");
      smsToast(rd.data);
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="create-document-modal">
      <div className={`bg-white rounded-2xl w-full max-h-[90vh] overflow-y-auto ${(isTax || isContract || isQuestionnaire) ? "max-w-2xl" : "max-w-lg"}`}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-rose-50 to-pink-50">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Send className="w-5 h-5 text-rose-600" />
            Envoyer un document à signer
          </h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {createdLink ? (
          <div className="p-5 space-y-4" data-testid="mandate-created-link-panel">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <p className="text-sm font-semibold text-emerald-800">{isTax ? "Rapport d'impôt créé !" : isContract ? "Contrat de services créé !" : isQuestionnaire ? "Questionnaire créé !" : "Mandat fiscal créé !"}</p>
              <p className="text-xs text-emerald-700 mt-1">Voici le lien magique — le client n'a besoin d'aucune connexion :</p>
            </div>
            <div className="flex gap-2">
              <input readOnly value={createdLink} className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-xs bg-slate-50" data-testid="mandate-created-link" />
              <button onClick={() => copyLink(createdLink)}
                className="px-3 py-2 rounded-lg bg-slate-800 text-white text-sm inline-flex items-center gap-1.5 hover:bg-slate-900"
                data-testid="mandate-copy-created-link">
                <Copy className="w-4 h-4" /> Copier
              </button>
            </div>
            <button onClick={onClose} className="w-full px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50">
              Fermer
            </button>
          </div>
        ) : (
        <>
        <div className="p-5 space-y-4">
          {loading ? (
            <div className="text-center py-8 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  <FileText className="w-4 h-4 inline mr-1" /> Modèle
                </label>
                <select
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="select-template"
                >
                  <option value="">— Sélectionnez un modèle —</option>
                  <option value={FISCAL_FORM}>📋 Mandat fiscal — Formulaire de renseignements (à remplir + signer)</option>
                  <option value={TAX_REPORT}>📊 Rapport d'impôt — lettre + confirmation (montants manuels, à signer)</option>
                  <option value={SERVICE_CONTRACT}>📝 Contrat de services — contenu modifiable (cases à cocher + signature)</option>
                  <option value={QUESTIONNAIRE}>❓ Questionnaire — questions/réponses + sections texte (à remplir + signer)</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                {templates.length === 0 && !isSpecial && (
                  <p className="text-xs text-amber-700 mt-1">
                    Aucun modèle PDF de type "document". Allez dans l'onglet <Link to="/admin/client-documents?tab=modeles" className="underline">Modèles PDF</Link> pour en créer un.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  <User className="w-4 h-4 inline mr-1" /> Client
                </label>
                <select
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="select-client"
                >
                  <option value="">— Sélectionnez un client —</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.first_name} {c.last_name} {c.email ? `(${c.email})` : ""}
                    </option>
                  ))}
                </select>
                {!isSpecial && <p className="text-xs text-slate-400 mt-1">Seuls les clients avec portail activé apparaissent.</p>}
              </div>

              {isFiscal && (
                <div className="bg-blue-50/60 border border-blue-200 rounded-xl p-3 space-y-3" data-testid="fiscal-mandate-options">
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-semibold text-slate-700">Année d'imposition</label>
                    <input type="number" value={taxYear} onChange={(e) => setTaxYear(e.target.value)}
                      className="w-24 border border-slate-300 rounded-lg px-3 py-1.5 text-sm" data-testid="mandate-tax-year" />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={prefill} onChange={(e) => setPrefill(e.target.checked)}
                      className="w-4 h-4 accent-blue-900" data-testid="mandate-prefill" />
                    Pré-remplir avec les infos connues du client (nom, adresse, courriel...)
                  </label>
                  <div className="text-sm font-semibold text-slate-700">Comment l'envoyer ?</div>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)}
                      className="w-4 h-4 accent-blue-900" data-testid="mandate-send-email" />
                    Courriel avec lien magique (aucune connexion requise)
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={showInPortal} onChange={(e) => setShowInPortal(e.target.checked)}
                      className="w-4 h-4 accent-blue-900" data-testid="mandate-show-portal" />
                    Afficher dans le portail client (Documents à signer)
                  </label>
                  {smsOptIn("accent-blue-900")}
                  <p className="text-[11px] text-slate-500">Dans tous les cas, le lien magique sera copiable après la création.</p>
                </div>
              )}

              {isTax && (
                <div className="bg-indigo-50/50 border border-indigo-200 rounded-xl p-3 space-y-3" data-testid="tax-report-options">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div>
                      <label className="text-xs font-semibold text-slate-600 block mb-1">Civilité</label>
                      <select value={taxCivility} onChange={(e) => setTaxCivility(e.target.value)}
                        className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm" data-testid="tax-civility">
                        <option>Madame</option>
                        <option>Monsieur</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 block mb-1">Année d'imposition</label>
                      <input type="number" value={taxYear} onChange={(e) => setTaxYear(e.target.value)}
                        className="w-24 border border-slate-300 rounded-lg px-3 py-1.5 text-sm" data-testid="tax-year" />
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Nom, adresse et date de la lettre sont remplis automatiquement selon le client sélectionné.
                    Entrez tous les montants manuellement (tels qu'ils apparaîtront, ex : 73 710.70) :
                  </p>
                  <div>
                    <p className="text-xs font-bold text-slate-700 mb-1.5">Revenus</p>
                    <div className="grid grid-cols-2 gap-2">
                      {AMOUNT_FIELDS.revenus.map(([k, label]) => (
                        <div key={k}>
                          <label className="text-[10px] text-slate-500 block">{label}</label>
                          <input value={amounts[k] || ""} onChange={(e) => setAmt(k, e.target.value)} placeholder="0.00"
                            className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm" data-testid={`tax-amt-${k}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                  {[["fed", "Fédéral"], ["prov", "Provincial"]].map(([prefix, title]) => (
                    <div key={prefix}>
                      <p className="text-xs font-bold text-slate-700 mb-1.5">{title}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {AMOUNT_FIELDS[prefix].map(([k, label]) => (
                          <div key={k}>
                            <label className="text-[10px] text-slate-500 block">{label}</label>
                            <input value={amounts[k] || ""} onChange={(e) => setAmt(k, e.target.value)} placeholder="0.00"
                              className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm" data-testid={`tax-amt-${k}`} />
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5">
                        <span className="text-[10px] text-slate-500">Résultat :</span>
                        {["solde", "remboursement"].map((t) => (
                          <label key={t} className="text-xs inline-flex items-center gap-1 cursor-pointer capitalize">
                            <input type="radio" checked={amounts[`${prefix}_type`] === t}
                              onChange={() => setAmt(`${prefix}_type`, t)} className="accent-blue-900"
                              data-testid={`tax-${prefix}-type-${t}`} />
                            {t}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div>
                    <p className="text-xs font-bold text-slate-700 mb-1.5">Combiné (année complète)</p>
                    <div className="flex items-center gap-3 flex-wrap">
                      <input value={amounts.combine || ""} onChange={(e) => setAmt("combine", e.target.value)} placeholder="0.00"
                        className="w-32 border border-slate-300 rounded-lg px-2 py-1.5 text-sm" data-testid="tax-amt-combine" />
                      {["solde", "remboursement"].map((t) => (
                        <label key={t} className="text-xs inline-flex items-center gap-1 cursor-pointer capitalize">
                          <input type="radio" checked={amounts.combine_type === t}
                            onChange={() => setAmt("combine_type", t)} className="accent-blue-900"
                            data-testid={`tax-combine-type-${t}`} />
                          {t}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-slate-700">Comment l'envoyer ?</div>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)}
                      className="w-4 h-4 accent-blue-900" data-testid="tax-send-email" />
                    Courriel avec lien magique (aucune connexion requise)
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={showInPortal} onChange={(e) => setShowInPortal(e.target.checked)}
                      className="w-4 h-4 accent-blue-900" data-testid="tax-show-portal" />
                    Afficher dans le portail client (Documents à signer)
                  </label>
                  {smsOptIn("accent-blue-900")}
                  <p className="text-[11px] text-slate-500">La date de signature sera inscrite automatiquement quand le client signera. Le lien magique sera copiable après la création.</p>
                </div>
              )}

              {isContract && contract && (
                <>
                  <ServiceContractEditor value={contract} onChange={setContract} />
                  <div className="bg-sky-50/50 border border-sky-200 rounded-xl p-3 space-y-2">
                    <div className="text-sm font-semibold text-slate-700">Comment l'envoyer ?</div>
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)}
                        className="w-4 h-4 accent-blue-900" data-testid="contract-send-email" />
                      Courriel avec lien magique (aucune connexion requise)
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={showInPortal} onChange={(e) => setShowInPortal(e.target.checked)}
                        className="w-4 h-4 accent-blue-900" data-testid="contract-show-portal" />
                      Afficher dans le portail client (Documents à signer)
                    </label>
                    {smsOptIn("accent-blue-900")}
                    <p className="text-[11px] text-slate-500">La date de signature du client sera inscrite automatiquement. Le lien magique sera copiable après la création.</p>
                  </div>
                </>
              )}

              {isQuestionnaire && questionnaire && (
                <>
                  <QuestionnaireEditor value={questionnaire} onChange={setQuestionnaire} />
                  <div className="bg-violet-50/50 border border-violet-200 rounded-xl p-3 space-y-2">
                    <div className="text-sm font-semibold text-slate-700">Comment l'envoyer ?</div>
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)}
                        className="w-4 h-4 accent-blue-900" data-testid="qn-send-email" />
                      Courriel avec lien magique (aucune connexion requise)
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={showInPortal} onChange={(e) => setShowInPortal(e.target.checked)}
                        className="w-4 h-4 accent-blue-900" data-testid="qn-show-portal" />
                      Afficher dans le portail client (Documents à signer)
                    </label>
                    {smsOptIn("accent-blue-900")}
                    <p className="text-[11px] text-slate-500">La date de signature du client sera inscrite automatiquement. Le lien magique sera copiable après la création.</p>
                  </div>
                </>
              )}

              {!isSpecial && templateId && (
                <div className="bg-rose-50/50 border border-rose-200 rounded-xl p-3 space-y-2" data-testid="document-send-options">
                  <div className="text-sm font-semibold text-slate-700">Comment l'envoyer ?</div>
                  <p className="text-[11px] text-slate-500">Le document est déposé dans le portail client (Documents à signer) et le client est avisé par courriel.</p>
                  {smsOptIn("accent-rose-700")}
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Note pour le client (facultatif)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Ex : Merci de signer ce mandat avant le 31 mars."
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="input-notes"
                />
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50">
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !templateId || !clientId}
            className="px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="submit-create-document"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {isFiscal ? "Créer le mandat" : isTax ? "Créer le rapport" : isContract ? "Créer le contrat" : isQuestionnaire ? "Créer le questionnaire" : "Envoyer au client"}
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

const API_BY_TYPE = {
  fiscal: "fiscal-mandates",
  tax_report: "tax-report-letters",
  contract: "service-contracts",
  questionnaire: "questionnaires",
  document: "client-documents",
};
const DRIVE_DOC_TYPE = {
  fiscal: "fiscal_mandate",
  tax_report: "tax_report",
  contract: "service_contract",
  questionnaire: "questionnaire",
  document: "client_document",
};

function ReminderChip({ info, cfg }) {
  const sent = (info.reminders || []).filter((r) => r.step !== "alert").length;
  const alerted = (info.reminders || []).some((r) => r.step === "alert");
  const label = alerted ? `J+${info.age_days} · alerte envoyée`
    : sent ? `J+${info.age_days} · ${sent} rappel${sent > 1 ? "s" : ""} envoyé${sent > 1 ? "s" : ""}`
    : info.next ? `J+${info.age_days} · rappel dans ${info.next.in_days} j` : `J+${info.age_days}`;
  const off = cfg && cfg.enabled === false;
  return (
    <span className={`px-1.5 py-0.5 rounded border text-[10px] ${alerted ? "bg-rose-50 text-rose-700 border-rose-200" : sent ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}
      title={off ? "Relance auto désactivée (Paramètres → Notifications)" : "Relance auto des documents à signer"} data-testid={`doc-reminder-${info.id}`}>
      {off ? "Relance auto désactivée" : label}
    </span>
  );
}

// ── Page principale ──────────────────────────────────────────
export default function ClientDocumentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "modeles" ? "modeles" : "documents";
  const setTab = (t) => setSearchParams(t === "modeles" ? { tab: "modeles" } : {});
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  // « Marquer traité » : sort un document en attente de la pastille sans attendre la signature
  const { isOverridden, markRead, markUnread } = useReadOverrides("client_document");
  // Relance auto (Batch #14.263) : rappels envoyés / prochaine étape par document en attente
  const [reminders, setReminders] = useState({ config: null, byId: {} });
  const [smsDoc, setSmsDoc] = useState(null); // Batch #14.336 — « Envoyer par texto » (lien seulement)
  const refreshBadges = () => window.dispatchEvent(new CustomEvent("badges:refresh"));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter && statusFilter !== "archived" ? `?status=${statusFilter}` : "";
      const fiscalParams = statusFilter === "signed" ? "?status=completed" : (statusFilter && statusFilter !== "archived" ? `?status=${statusFilter}` : "");
      const [r1, r2, r3, r4, r5] = await Promise.all([
        axios.get(`${API}/admin/client-documents${params}`, { headers: authH() }),
        axios.get(`${API}/admin/fiscal-mandates${fiscalParams}`, { headers: authH() }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/admin/tax-report-letters${fiscalParams}`, { headers: authH() }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/admin/service-contracts${fiscalParams}`, { headers: authH() }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/admin/questionnaires${fiscalParams}`, { headers: authH() }).catch(() => ({ data: { items: [] } })),
      ]);
      const docs = (r1.data?.items || []).map((d) => ({ ...d, _type: "document" }));
      const mandates = (r2.data?.items || []).map((m) => ({
        ...m,
        _type: "fiscal",
        template_name: `Mandat fiscal ${m.tax_year} — Formulaire de renseignements`,
      }));
      const taxReports = (r3.data?.items || []).map((t) => ({
        ...t,
        _type: "tax_report",
        template_name: `Rapport d'impôt ${t.tax_year} — lettre + confirmation`,
      }));
      const contracts = (r4.data?.items || []).map((ct) => ({
        ...ct,
        _type: "contract",
        template_name: ct.title || "Contrat de services",
      }));
      const questionnaires = (r5.data?.items || []).map((qn) => ({
        ...qn,
        _type: "questionnaire",
        template_name: qn.title || "Questionnaire",
      }));
      const merged = [...docs, ...mandates, ...taxReports, ...contracts, ...questionnaires].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      // Batch #14.337 — archivés à part : « Archivés » ne montre qu'eux, les autres filtres les cachent
      setItems(merged.filter((d) => (statusFilter === "archived" ? !!d.archived_at : !d.archived_at)));
      axios.get(`${API}/admin/signature-reminders`, { headers: authH() })
        .then((r) => setReminders({ config: r.data.config, byId: Object.fromEntries((r.data.items || []).map((it) => [it.id, it])) }))
        .catch(() => {});
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (d) => {
    const signed = d.status === "signed" || d.status === "completed";
    const drive = (d.drive_uploads || []).length;
    const warn = signed
      ? (drive ? `\n\nClassé dans Google Drive (${drive} envoi${drive > 1 ? "s" : ""}) — le PDF signé y reste.` : "\n\n⚠️ Ce document n'a PAS été classé dans Google Drive : le PDF signé sera perdu définitivement.")
      : "";
    if (!window.confirm(`Supprimer définitivement « ${d.template_name} » (${d.client_name}) ?${warn}`)) return;
    try {
      await axios.delete(`${API}/admin/signed-docs/${d._type}/${d.id}`, { headers: authH() });
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression refusée");
    }
  };

  // Batch #14.337 — archiver (ménage après classement) / désarchiver / tout archiver
  const handleArchive = async (d, archived = true) => {
    try {
      await axios.post(`${API}/admin/signed-docs/${d._type}/${d.id}/archive`, { archived }, { headers: authH() });
      toast.success(archived ? "Archivé — visible sous le filtre « Archivés »" : "Remis dans la liste");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action refusée");
    }
  };
  const handleArchiveAllSigned = async () => {
    const n = items.filter((d) => (d.status === "signed" || d.status === "completed") && !d.archived_at).length;
    if (!n || !window.confirm(`Archiver les ${n} document(s) signé(s) de la liste ? Ils restent consultables sous « Archivés ».`)) return;
    try {
      const r = await axios.post(`${API}/admin/signed-docs/archive-signed`, {}, { headers: authH() });
      toast.success(`${r.data.archived} document(s) archivé(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action refusée");
    }
  };

  // Aperçu : le PDF (original ou signé) s'ouvre dans un nouvel onglet
  const handlePreview = (d) => {
    axios.get(`${API}/admin/${API_BY_TYPE[d._type]}/${d.id}/pdf`, { headers: authH(), responseType: "blob" }).then((r) => {
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      // « noopener » ferait toujours renvoyer null à window.open (faux « Fenêtre bloquée ») : on coupe opener à la main
      const w = window.open(url, "_blank");
      if (!w) toast.error("Fenêtre bloquée — autorisez les fenêtres surgissantes");
      else { try { w.opener = null; } catch (_) { /* ignore */ } }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }).catch(() => toast.error("Aperçu indisponible"));
  };

  const handleDownload = (d) => {
    const url = `${API}/admin/${API_BY_TYPE[d._type]}/${d.id}/pdf`;
    axios.get(url, { headers: authH(), responseType: "blob" }).then((r) => {
      const blobUrl = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      const done = d.status === "signed" || d.status === "completed";
      a.download = `${d.template_name}-${done ? "signé" : "original"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    }).catch(() => toast.error("Téléchargement échoué"));
  };

  return (
    <div className="space-y-5 p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-client-documents">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileSignature className="w-6 h-6 text-rose-600" />
            Documents à signer
          </h2>
          <p className="text-sm text-slate-500">Envoyez vos mandats et modèles PDF aux clients pour signature électronique</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowBulk(true)}
            className="px-4 py-2 rounded-lg bg-white border border-rose-300 text-rose-700 text-sm font-semibold hover:bg-rose-50 inline-flex items-center gap-1.5"
            title="Début de saison d'impôts : envoyer le formulaire de renseignements fiscaux à plusieurs clients en un clic"
            data-testid="open-bulk-mandate-modal"
          >
            <Users className="w-4 h-4" /> Envoi groupé
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 inline-flex items-center gap-1.5"
            data-testid="open-create-modal"
          >
            <Plus className="w-4 h-4" /> Nouveau document
          </button>
        </div>
      </div>
      {showBulk && <BulkMandateModal onClose={() => { setShowBulk(false); load(); }} onDone={load} />}

      {/* Onglets : documents envoyés | modèles PDF (ex-tuile « Modèles PDF ») */}
      <div className="flex gap-1 border-b border-slate-200" data-testid="client-docs-tabs">
        {[["documents", "Documents envoyés"], ["modeles", "Modèles PDF"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${tab === id ? "border-rose-600 text-rose-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid={`client-docs-tab-${id}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "modeles" && <PdfTemplatesTab />}

      {tab === "documents" && (<>
      {/* Filtres */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3 flex-wrap">
        <label className="text-xs font-semibold text-slate-600">Statut :</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm"
          data-testid="filter-status"
        >
          <option value="">Tous</option>
          <option value="pending">En attente</option>
          <option value="signed">Signés</option>
          <option value="archived">Archivés</option>
        </select>
        {statusFilter !== "archived" && statusFilter !== "pending" && items.some((d) => (d.status === "signed" || d.status === "completed") && !d.archived_at) && (
          <button
            onClick={handleArchiveAllSigned}
            className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50 inline-flex items-center gap-1.5"
            title="Archiver d'un coup tous les documents signés de la liste (ménage après classement)"
            data-testid="archive-all-signed-btn"
          >
            <Archive className="w-3.5 h-3.5" /> Archiver les signés
          </button>
        )}
        {statusFilter === "archived" && (
          <span className="text-[11px] text-slate-500" data-testid="archived-hint">Documents classés : vous pouvez les remettre dans la liste ou les supprimer définitivement.</span>
        )}
        <span className="text-xs text-slate-500 ml-auto" data-testid="documents-count">{items.length} document(s)</span>
      </div>

      {loading && (
        <div className="text-center py-12 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-12 text-center" data-testid="empty-state">
          <FileSignature className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">Aucun document envoyé pour ce filtre.</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-2" data-testid="documents-list">
          {items.map((d) => (
            <div
              key={`${d._type}-${d.id}`}
              className={`bg-white border rounded-xl p-4 hover:shadow-md transition flex flex-col sm:flex-row sm:items-center gap-3 ${d.archived_at ? "border-slate-200 bg-slate-50/60" : "border-slate-200"}`}
              data-testid={`document-${d.id}`}
            >
              <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${d._type === "fiscal" ? "bg-blue-100" : d._type === "tax_report" ? "bg-indigo-100" : d._type === "contract" ? "bg-sky-100" : d._type === "questionnaire" ? "bg-violet-100" : "bg-rose-100"}`}>
                {d._type === "fiscal"
                  ? <ClipboardList className="w-5 h-5 text-blue-800" />
                  : d._type === "tax_report"
                    ? <FileText className="w-5 h-5 text-indigo-700" />
                    : d._type === "contract"
                      ? <FileSignature className="w-5 h-5 text-sky-700" />
                      : d._type === "questionnaire"
                        ? <ClipboardList className="w-5 h-5 text-violet-700" />
                        : <FileSignature className="w-5 h-5 text-rose-700" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 break-words sm:truncate" data-testid={`document-title-${d.id}`}>{d.template_name}</div>
                <div className="text-xs text-slate-500 flex items-center gap-x-3 gap-y-1 flex-wrap">
                  <span className="flex items-center gap-1 min-w-0">
                    <User className="w-3 h-3 shrink-0" /> <span className="truncate">{d.client_name}</span>
                  </span>
                  {d.client_email && (
                    <span className="flex items-center gap-1 min-w-0">
                      <Mail className="w-3 h-3 shrink-0" /> <span className="truncate">{d.client_email}</span>
                    </span>
                  )}
                  <StatusBadge status={d.status} />
                  {d.archived_at && (
                    <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 text-[10px] inline-flex items-center gap-1" data-testid={`doc-archived-${d.id}`}>
                      <Archive className="w-3 h-3" /> Archivé le {fmtDate(d.archived_at)}
                    </span>
                  )}
                  {(d.drive_uploads || []).length > 0 && (
                    <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]" title={`Classé dans Google Drive le ${fmtDate(d.drive_uploads[d.drive_uploads.length - 1].at)}`} data-testid={`doc-drive-${d.id}`}>
                      Classé Drive
                    </span>
                  )}
                  {d.status === "pending" && reminders.byId[d.id] && <ReminderChip info={reminders.byId[d.id]} cfg={reminders.config} />}
                  {d._type === "document" && d.status === "pending" && isOverridden(d.id) && (
                    <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]" data-testid={`doc-handled-${d.id}`}>
                      Traité (hors pastille)
                    </span>
                  )}
                  <span className="whitespace-nowrap">Envoyé le {fmtDate(d.created_at)}</span>
                  {(d.signature?.signed_at || d.signed_at) && <span className="whitespace-nowrap">Signé le {fmtDate(d.signature?.signed_at || d.signed_at)}</span>}
                </div>
                {d.notes && (
                  <p className="text-xs text-slate-400 italic mt-1 truncate">"{d.notes}"</p>
                )}
              </div>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0 w-full sm:w-auto">
                {d._type === "document" && d.status === "pending" && (
                  <button
                    onClick={async () => { if (isOverridden(d.id)) await markUnread(d.id); else await markRead(d.id); refreshBadges(); }}
                    className={`px-3 py-1.5 rounded-lg border text-sm inline-flex items-center gap-1.5 ${isOverridden(d.id) ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}
                    title={isOverridden(d.id) ? "Remettre dans la pastille" : "Le client ne signera pas : retirer de la pastille"}
                    data-testid={`doc-toggle-handled-${d.id}`}
                  >
                    {isOverridden(d.id) ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                    {isOverridden(d.id) ? "Traité" : "Marquer traité"}
                  </button>
                )}
                {/* Batch #14.216 — Classement manuel dans Google Drive */}
                <DriveSendButton
                  docType={DRIVE_DOC_TYPE[d._type]}
                  docId={d.id}
                  defaultExpertise={d._type === "contract" ? "entrepreneur" : "fiscal"}
                />
                {d._type !== "document" && d.magic_link && (
                  <button
                    onClick={() => copyLink(d.magic_link)}
                    className="px-3 py-1.5 rounded-lg border border-blue-300 text-blue-800 text-sm hover:bg-blue-50 inline-flex items-center"
                    title="Copier le lien magique"
                    data-testid={`copy-link-${d.id}`}
                  >
                    <Link2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => setSmsDoc(d)}
                  className="px-3 py-1.5 rounded-lg border border-orange-300 text-orange-700 text-sm hover:bg-orange-50 inline-flex items-center"
                  title={`Envoyer par texto (lien seulement — texte à valider)${d.sms_sent_at ? ` — déjà envoyé le ${fmtDate(d.sms_sent_at)}` : ""}`}
                  data-testid={`sms-${d.id}`}
                >
                  <Smartphone className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handlePreview(d)}
                  className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 inline-flex items-center"
                  title={d.status === "signed" || d.status === "completed" ? "Visualiser le PDF signé" : "Visualiser le PDF"}
                  data-testid={`preview-${d.id}`}
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDownload(d)}
                  className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 inline-flex items-center gap-1.5"
                  title={d.status === "signed" || d.status === "completed" ? "Télécharger le PDF signé" : "Télécharger le PDF original"}
                  data-testid={`download-${d.id}`}
                >
                  <Download className="w-4 h-4" />
                </button>
                {d.status === "pending" && (
                  <button
                    onClick={() => handleDelete(d)}
                    className="px-3 py-1.5 rounded-lg border border-red-300 text-red-700 text-sm hover:bg-red-50 inline-flex items-center"
                    title="Supprimer"
                    data-testid={`delete-${d.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                {/* Batch #14.337 — ménage : archiver un document signé, puis le supprimer depuis « Archivés » */}
                {d.status !== "pending" && !d.archived_at && (
                  <button
                    onClick={() => handleArchive(d, true)}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-100 inline-flex items-center gap-1.5"
                    title="Archiver (retire de la liste — reste consultable sous « Archivés »)"
                    data-testid={`archive-${d.id}`}
                  >
                    <Archive className="w-4 h-4" /><span className="hidden sm:inline">Archiver</span>
                  </button>
                )}
                {d.archived_at && (
                  <>
                    <button
                      onClick={() => handleArchive(d, false)}
                      className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-100 inline-flex items-center gap-1.5"
                      title="Remettre dans la liste"
                      data-testid={`unarchive-${d.id}`}
                    >
                      <ArchiveRestore className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(d)}
                      className="px-3 py-1.5 rounded-lg border border-red-300 text-red-700 text-sm hover:bg-red-50 inline-flex items-center gap-1.5"
                      title="Supprimer définitivement"
                      data-testid={`delete-${d.id}`}
                    >
                      <Trash2 className="w-4 h-4" /><span className="hidden sm:inline">Supprimer</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      </>)}

      {showCreate && (
        <CreateDocumentModal onClose={() => { setShowCreate(false); load(); }} onCreated={load} />
      )}
      {smsDoc && <SmsSendModal kind={smsDoc._type} id={smsDoc.id} title={smsDoc.template_name} onClose={() => setSmsDoc(null)} onSent={load} />}
    </div>
  );
}
